import { useToken } from "utils/context";
import "./Section111Analytics.scss";
import { useGetSection111AnalyticsQuery } from "services/api";
import { useCallback, useMemo } from "react";
import { Controller } from "react-hook-form";

import { useForm } from "react-hook-form";
import { useGetRresQuery } from "services/rre-api";
import Card from "components/Card/Card";
import { getHostName } from "api-helpers/getHostName.ts";
import type { OptionType } from "components/inputs";
import { MultiSelect } from "components/inputs";
import isNumber from "lodash/isNumber";
import keyBy from "lodash/keyBy";
import type { FieldErrors } from "react-hook-form";
import { Tooltip } from "components/Tooltip/Tooltip";

const getClaimIdsLink = (claimIds: number[]) => {
  return `${getHostName()}/section-111/claims?ids=${claimIds.join(",")}`;
};

const ActionItem = ({
  title,
  value,
  claimIds,
  classType,
  tooltipInfo,
}: {
  title: string;
  value: number;
  claimIds?: number[];
  classType?: string;
  tooltipInfo: string;
}) => {
  const handleClickLink = () => {
    if (claimIds && claimIds.length > 0) {
      window.open(getClaimIdsLink(claimIds), "_blank");
    }
  };

  return (
    <Tooltip content={tooltipInfo}>
      <div className="data-values" onClick={handleClickLink}>
        <label>
          <span className={classType ? classType : ""}>{value}</span>
        </label>
        <figure>{title}</figure>
      </div>
    </Tooltip>
  );
};

const BarGraphItem = ({
  title,
  classType,
  value,
  width,
}: {
  title: string;
  classType: string;
  value: number;
  width: number;
}) => {
  return (
    <div className={`grid-layout ai-c ${classType}`}>
      <span className="year">{title}</span>
      <div
        className="bar"
        style={{
          width: `${width}%`,
        }}
        data-percentage={value}
      ></div>
    </div>
  );
};

const calculatePercentageMapping = (dataGroup: Record<string, number>) => {
  const maxValue = Math.max(...Object.values(dataGroup));
  if (maxValue === 0)
    return Object.fromEntries(Object.keys(dataGroup).map((key) => [key, 0]));
  return Object.fromEntries(
    Object.entries(dataGroup).map(([key, value]) => [
      key,
      (value / maxValue) * 100,
    ]),
  );
};

const Dashboard = ({ results, detailedResults }) => {
  const queryPercentageMapping = calculatePercentageMapping(
    results?.query || {},
  );
  const reportingPercentageMapping = calculatePercentageMapping(
    results?.report || {},
  );
  const auditHistoryPercentageMapping = calculatePercentageMapping(
    results?.audit_history || {},
  );
  return (
    <div className="swim-wrapper h-100 content-section-padding">
      <div className="grid-layout gl-2 section111 swimlanes">
        <div className="swim-headers border-bottom border-right">
          Action Items
        </div>
        <div className="swim-headers border-bottom">Metrics</div>

        <div className="task-history-wrapper grid-3-high">
          <div className="swim-column">
            <div className="data-block action-list">
              <ActionItem
                title={"Missing Query Fields"}
                value={results?.action_items?.missing_query_fields}
                claimIds={detailedResults?.action_items?.missing_query_fields}
                classType={null}
                tooltipInfo={
                  "Claim data is missing requisite information to run Medicare query. Review for completion."
                }
              />
            </div>
            <div className="data-block action-list">
              <ActionItem
                title={"Reporting Validation Errors"}
                value={results?.action_items?.missing_fields}
                claimIds={detailedResults?.action_items?.missing_fields}
                classType={null}
                tooltipInfo={
                  "Claim data is missing required fields in order to report or has other validation errors. Review for completion."
                }
              />
            </div>
            <div className="data-block action-list">
              <ActionItem
                title={"CMS Reporting Errors"}
                value={results?.action_items?.cms_reporting_errors}
                claimIds={detailedResults?.action_items?.cms_reporting_errors}
                classType={null}
                tooltipInfo={
                  "Disposition errors returned from CMS. Review for corrections and possible resubmission."
                }
              />
            </div>
            <div className="data-block action-list">
              <ActionItem
                title={"CMP Warnings"}
                value={results?.action_items?.cmp_warning}
                claimIds={detailedResults?.action_items?.cmp_warning}
                classType={null}
                tooltipInfo={
                  "Claims that are more than 275 days since occurrence date."
                }
              />
            </div>
            <div className="data-block action-list">
              <ActionItem
                title={"CMP Errors"}
                value={results?.action_items?.cmp_error}
                claimIds={detailedResults?.action_items?.cmp_error}
                classType={null}
                tooltipInfo={
                  "Claims that are more than 365 days since occurrence date."
                }
              />
            </div>
            <div className="data-block action-list">
              <ActionItem
                title={"ORM Term + No TPOC"}
                value={results?.action_items?.orm_term_and_no_tpoc}
                claimIds={detailedResults?.action_items?.orm_term_and_no_tpoc}
                classType={null}
                tooltipInfo={
                  "Claim data includes an ORM termination date with no TPOC. Ensure TPOC data has not been erroneously omitted."
                }
              />
            </div>
            <div className="data-block action-list">
              <ActionItem
                title={"TPOC + No ORM Term"}
                value={results?.action_items?.tpoc_and_no_orm_term}
                claimIds={detailedResults?.action_items?.tpoc_and_no_orm_term}
                classType={null}
                tooltipInfo={
                  "Claim data includes TPOC information with no ORM Termination date. Ensure ORM data has not been erroneously omitted."
                }
              />
            </div>
            <div className="data-block action-list">
              <ActionItem
                title={"Settlement Docs"}
                value={0}
                claimIds={[]}
                classType={null}
                tooltipInfo={
                  "Settlement documents on file with no TPOC Data entered. Review reporting data."
                }
              />
            </div>
            <div className="data-block action-list">
              <ActionItem
                title={"5yr Rule"}
                value={0}
                claimIds={[]}
                classType={null}
                tooltipInfo={
                  "Pay history and claim data indicate that WC exclusion has been met."
                }
              />
              <ActionItem
                title={"$750 WC Rule"}
                value={0}
                claimIds={[]}
                classType={null}
                tooltipInfo={
                  "Pay history and claim data indicate that WC exclusion has been met."
                }
              />
              <ActionItem
                title={"State Logic"}
                value={0}
                claimIds={[]}
                classType={null}
                tooltipInfo={
                  "Claim data indicates that state specific statute of limitations may have been met. Review for possible ORM termination."
                }
              />
            </div>
          </div>
        </div>

        <div className="swim-column border-bottom no-pd">
          <div className="swim-sub-header">Query</div>

          <section className="bar-graph bar-graph-horizontal">
            <BarGraphItem
              title={"First Time ME Hit"}
              classType={null}
              value={results?.query?.first_time_medicare_eligible_hit}
              width={queryPercentageMapping?.first_time_medicare_eligible_hit}
            />
            <BarGraphItem
              title={"Ready To Query"}
              classType={null}
              value={results?.query?.ready}
              width={queryPercentageMapping?.ready}
            />
            <BarGraphItem
              title={"Queried Last Quarter"}
              classType={null}
              value={results?.query?.last_quarter}
              width={queryPercentageMapping?.last_quarter}
            />
            <BarGraphItem
              title={"Year To Date"}
              classType={null}
              value={results?.query?.year_to_date}
              width={queryPercentageMapping?.year_to_date}
            />
            <BarGraphItem
              title={"All Time"}
              classType={null}
              value={results?.query?.all_time}
              width={queryPercentageMapping?.all_time}
            />
          </section>
        </div>

        <div className="swim-column border-bottom no-pd">
          <div className="swim-sub-header">Report</div>

          <section className="bar-graph bar-graph-horizontal">
            <BarGraphItem
              title={"Ready"}
              classType={null}
              value={results?.report?.ready}
              width={reportingPercentageMapping?.ready}
            />
            <BarGraphItem
              title={"Last Quarter"}
              classType={null}
              value={results?.report?.last_quarter}
              width={reportingPercentageMapping?.last_quarter}
            />
            <BarGraphItem
              title={"Year To Date"}
              classType={null}
              value={results?.report?.year_to_date}
              width={reportingPercentageMapping?.year_to_date}
            />
            <BarGraphItem
              title={"All Time"}
              classType={null}
              value={results?.report?.all_time}
              width={reportingPercentageMapping?.all_time}
            />
          </section>
        </div>

        <div className="swim-column border-bottom no-pd">
          <div className="swim-sub-header">Audit History</div>

          <section className="bar-graph bar-graph-horizontal">
            <BarGraphItem
              title={"Last 30 Days"}
              classType={null}
              value={results?.audit_history?.last_30_days}
              width={auditHistoryPercentageMapping?.last_30_days}
            />
            <BarGraphItem
              title={"Last 90 Days"}
              classType={null}
              value={results?.audit_history?.last_90_days}
              width={auditHistoryPercentageMapping?.last_90_days}
            />
            <BarGraphItem
              title={"Year To Date"}
              classType={null}
              value={results?.audit_history?.year_to_date}
              width={auditHistoryPercentageMapping?.year_to_date}
            />
          </section>
        </div>
      </div>
    </div>
  );
};

type RREMultiSelectProps = {
  onChange: (options: OptionType[]) => void;
  errors: FieldErrors;
  options: OptionType[];
  value: OptionType[] | undefined;
  fileData?: {
    status?: string;
    services?: OptionType[];
  };
  required?: boolean;
  disableRemove?: boolean;
  label?: string;
  name?: string;
};

export const RREMultiSelect = ({
  onChange,
  errors,
  options,
  value,
  required = true,
  disableRemove = false,
  label = "Select RREs",
  name = "rre_ids",
}: RREMultiSelectProps) => {
  const getValue = () => {
    if (!value) return [];

    return value.map(
      (selectedOption: {
        value: number | string;
        label: string;
        type_verbose?: string;
      }) => {
        // return the original option if it's not a numeric value
        if (!isNumber(selectedOption.value)) return selectedOption;

        // for numeric values, prioritize type_verbose if available
        if (selectedOption.type_verbose) {
          return { ...selectedOption, label: selectedOption.type_verbose };
        }

        // otherwise look up the correct label from options
        const optionsMap = keyBy(options, "value");
        const correctLabel =
          optionsMap[selectedOption.label]?.label || selectedOption.label;

        return { ...selectedOption, label: correctLabel };
      },
    );
  };

  return (
    <MultiSelect
      label={label}
      name={name}
      errors={errors}
      options={options}
      value={getValue()}
      onChange={onChange}
      required={required}
      disableClearAll={true}
      disableRemoveCondition={disableRemove}
    />
  );
};

type AnalyticsFormValues = {
  rre_ids: OptionType[];
};

export default function Section111Reporting() {
  const { accessToken } = useToken();
  // const [stateRreIds, setRreIds] = useState<number[]>([]);
  const {
    control,
    formState: { errors },
    setValue,
    getValues,
    trigger,
  } = useForm<AnalyticsFormValues>({
    defaultValues: {
      rre_ids: [],
    },
  });

  const { data: rres } = useGetRresQuery({ searchTerm: null }, { skip: false });

  const rresSelected = getValues("rre_ids");
  const rreIds = rresSelected?.map((rre) => rre.value) || [];

  // TODO, eventually remove this call and just use get details call and do a .length on the lists
  const { data: results, isLoading } = useGetSection111AnalyticsQuery(
    {
      rreIds: rreIds as string[],
    },
    {
      skip: !rreIds?.length,
    },
  );

  const { data: detailedResults, isLoading: isDetailedResultsLoading } =
    useGetSection111AnalyticsQuery(
      {
        rreIds: rreIds as string[],
        dataFormat: "DATA",
      },
      {
        skip: !accessToken || !rreIds?.length,
      },
    );

  const rreOptions = useMemo(() => {
    if (!rres?.results?.length) return [];
    return rres.results.map((rre) => ({
      value: rre.id,
      label: `${rre.rre_id} - ${rre.name}`,
    }));
  }, [rres]);

  const handleFieldChange = useCallback(
    async (options: { value: number; label: string }[]) => {
      setValue("rre_ids", options);
      trigger("rre_ids");
    },
    [setValue],
  );

  const content = useMemo(() => {
    if (rreIds?.length === 0 && !isLoading && !isDetailedResultsLoading) {
      return (
        <div className="no-tasks large">
          <div className="no-tasks-header">{"No Filters Applied"}</div>
          <div className="no-tasks-icons">
            <div>
              <span className="material-icons">content_paste</span>
            </div>
          </div>
        </div>
      );
    }
    return <Dashboard results={results} detailedResults={detailedResults} />;
  }, [results, rreIds, isLoading, isDetailedResultsLoading]);

  return (
    <>
      <Card heading={`Filter`}>
        <Controller
          control={control}
          name="rre_ids"
          rules={{ required: "RREs are required" }}
          render={({ field: { value } }) => (
            <RREMultiSelect
              label="Select RREs"
              name="rre_ids"
              errors={errors}
              options={rreOptions}
              value={rreOptions.filter((option) =>
                value?.map((v) => v.value).includes(option.value),
              )}
              onChange={handleFieldChange}
              required={true}
            />
          )}
        />
      </Card>
      {content}
    </>
  );
}
